import { useState, useCallback, useEffect } from 'react';
import {
  Wallet,
  Loader2,
  CheckCircle,
  Gift,
  FileCheck,
  ExternalLink,
  AlertCircle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/api/axios';
import {
  verifyAirdropEligibility,
  claimAirdrop,
  AirdropEligibilityResponse,
  verifyIcoInvestment,
  claimIco,
  IcoInvestmentResponse,
} from '@/api/claims';
import { useRewardsBalance } from '@/hooks/useRewardsBalance';
import {
  useAppKit,
  useAppKitAccount,
  useAppKitState,
  useDisconnect,
} from '@reown/appkit/react';
import { Button } from '../ui/button';
import { ChevronLeft } from 'lucide-react';

type ClaimSubTab = 'airdrops' | 'ico';

export function ClaimsView() {
  const { t } = useTranslation();
  const { refetch: refetchBalance } = useRewardsBalance();
  const [activeSubTab, setActiveSubTab] = useState<ClaimSubTab>('airdrops');

  // WalletConnect / AppKit: one connected wallet for both Airdrops and ICO
  const { open } = useAppKit();
  const { open: modalOpen } = useAppKitState();
  const { address: connectedAddress, isConnected } = useAppKitAccount();
  const { disconnect } = useDisconnect();

  const openConnectModal = () => {
    open({ view: 'Connect', namespace: 'eip155' });
  };

  const backToWalletList = () => {
    open({ view: 'Connect', namespace: 'eip155' });
  };

  const handleDisconnect = () => {
    disconnect();
  };

  return (
    <div className="space-y-6">
      {/* When connect modal is open (e.g. on QR step), show back to wallet selection */}
      {modalOpen && (
        <div
          className="flex items-center justify-center gap-2 py-2 px-4 rounded-xl bg-[#F0EFF5] dark:bg-white/5 border border-[#E2DFEA] dark:border-white/10"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          <button
            type="button"
            onClick={backToWalletList}
            className="flex items-center gap-2 text-sm font-medium text-[#936FEF] hover:text-[#7c5cd4] transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            {t('rewards.claims.backToWalletList')}
          </button>
        </div>
      )}
      <h2
        className="text-xl mb-4 text-[#2D2F7E] dark:text-white"
        style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
      >
        {t('rewards.claims.title')}
      </h2>

      {/* Sub-tabs: Airdrops | ICO Claims */}
      <div className="flex gap-2 border-b border-[#E2DFEA] dark:border-white/10 pb-2">
        <button
          type="button"
          onClick={() => setActiveSubTab('airdrops')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
            activeSubTab === 'airdrops'
              ? 'bg-[#936FEF] text-white'
              : 'text-[#464452] dark:text-white/70 hover:bg-[#F0EFF5] dark:hover:bg-white/5'
          }`}
          style={{ fontFamily: 'Sora, sans-serif' }}
        >
          <Gift className="w-4 h-4" />
          {t('rewards.claims.airdrops')}
        </button>
        <button
          type="button"
          onClick={() => setActiveSubTab('ico')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
            activeSubTab === 'ico'
              ? 'bg-[#936FEF] text-white'
              : 'text-[#464452] dark:text-white/70 hover:bg-[#F0EFF5] dark:hover:bg-white/5'
          }`}
          style={{ fontFamily: 'Sora, sans-serif' }}
        >
          <FileCheck className="w-4 h-4" />
          {t('rewards.claims.icoClaims')}
        </button>
      </div>

      {activeSubTab === 'airdrops' && (
        <AirdropsCard
          walletAddress={
            isConnected && connectedAddress ? connectedAddress : null
          }
          onConnectWallet={openConnectModal}
          onDisconnect={handleDisconnect}
          onClaimSuccess={refetchBalance}
        />
      )}
      {activeSubTab === 'ico' && (
        <IcoClaimsCard
          walletAddress={
            isConnected && connectedAddress ? connectedAddress : null
          }
          onConnectWallet={openConnectModal}
          onDisconnect={handleDisconnect}
          onClaimSuccess={refetchBalance}
        />
      )}
    </div>
  );
}

// --- Airdrops card ---

interface AirdropsCardProps {
  walletAddress: string | null;
  onConnectWallet: () => void;
  onDisconnect: () => void;
  onClaimSuccess: () => void;
}

function AirdropsCard({
  walletAddress,
  onConnectWallet,
  onDisconnect,
  onClaimSuccess,
}: AirdropsCardProps) {
  const { t } = useTranslation();
  const [eligibility, setEligibility] =
    useState<AirdropEligibilityResponse | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [claimedTxRef, setClaimedTxRef] = useState<string | null>(null);

  const checkEligibility = useCallback(async () => {
    if (!walletAddress) return;
    setIsChecking(true);
    setError(null);
    setEligibility(null);
    try {
      const res = await verifyAirdropEligibility(walletAddress);
      setEligibility(res);
    } catch (err) {
      const msg = getApiErrorMessage(
        err,
        t('rewards.claims.failedToCheckAirdrop')
      );
      setError(msg);
      toast.error(msg);
    } finally {
      setIsChecking(false);
    }
  }, [walletAddress]);

  useEffect(() => {
    if (walletAddress && eligibility === null && !isChecking && !error) {
      checkEligibility();
    }
  }, [walletAddress, eligibility, isChecking, error, checkEligibility]);

  const handleClaim = async (campaignId?: string) => {
    if (!walletAddress) return;
    setIsClaiming(true);
    setError(null);
    try {
      const res = await claimAirdrop({ walletAddress, campaignId });
      if (res.success) {
        toast.success(t('rewards.claims.airdropClaimedToast'), {
          description: t('rewards.claims.airdropClaimedDesc', {
            amount: res.claimedAmount,
          }),
        });
        if (res.transactionReference) setClaimedTxRef(res.transactionReference);
        onClaimSuccess();
        setEligibility(null);
        checkEligibility();
      } else {
        toast.error(res.message || t('rewards.claims.claimFailed'));
      }
    } catch (err) {
      const msg = getApiErrorMessage(
        err,
        t('rewards.claims.failedToClaimAirdrop')
      );
      setError(msg);
      toast.error(msg);
    } finally {
      setIsClaiming(false);
    }
  };

  const unclaimedCampaigns =
    eligibility?.campaigns?.filter((c) => c.status === 'unclaimed') ?? [];
  const totalClaimable = eligibility?.totalClaimablePet ?? 0;
  const allClaimed =
    eligibility?.eligible &&
    unclaimedCampaigns.length === 0 &&
    totalClaimable === 0;

  return (
    <div className="rounded-2xl border border-[#E2DFEA] dark:border-white/10 bg-white dark:bg-[#16151D] p-6">
      <p
        className="text-[#464452] dark:text-white/70 mb-6"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        {t('rewards.claims.verifyWalletAirdrop')}
      </p>

      {!walletAddress ? (
        <div className="flex flex-col items-center gap-4">
          <Button
            onClick={onConnectWallet}
            className="bg-[#936FEF] hover:bg-[#7c5cd4] text-white gap-2"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
          >
            <Wallet className="w-5 h-5" />
            {t('rewards.claims.connectWallet')}
          </Button>
          <p
            className="text-xs text-[#464452] dark:text-white/60"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('rewards.claims.supportedWallets')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span
                className="text-sm font-mono text-[#464452] dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}
              </span>
              <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                <CheckCircle className="w-3.5 h-3.5" />
                {t('rewards.claims.walletConnected')}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDisconnect}
              className="text-[#464452] dark:text-white/70"
            >
              {t('rewards.claims.disconnect')}
            </Button>
          </div>

          {isChecking && (
            <div className="flex items-center gap-2 py-6 text-[#464452] dark:text-white/70">
              <Loader2 className="w-5 h-5 animate-spin text-[#936FEF]" />
              <span style={{ fontFamily: 'Inter, sans-serif' }}>
                {t('rewards.claims.checkingEligibility')}
              </span>
            </div>
          )}

          {error && !isChecking && (
            <div className="flex items-start gap-2 p-4 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50">
              <AlertCircle className="w-5 h-5 shrink-0 text-red-600 dark:text-red-400" />
              <p
                className="text-sm text-red-700 dark:text-red-300"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {error}
              </p>
            </div>
          )}

          {eligibility && !eligibility.eligible && !isChecking && (
            <div className="rounded-xl p-6 bg-[#F0EFF5] dark:bg-white/5 border border-[#E2DFEA] dark:border-white/10 text-center">
              <p
                className="text-[#2D2F7E] dark:text-white mb-2"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
              >
                {t('rewards.claims.noActiveAirdrops')}
              </p>
              <a
                href="#"
                className="text-sm text-[#936FEF] hover:underline"
                style={{ fontFamily: 'Inter, sans-serif' }}
                onClick={(e) => {
                  e.preventDefault();
                  toast.info(t('rewards.claims.airdropsHowInfo'));
                }}
              >
                {t('rewards.claims.howAirdropsWork')}
              </a>
            </div>
          )}

          {eligibility?.eligible && !isChecking && (
            <div className="space-y-4">
              {unclaimedCampaigns.length > 0 ? (
                <>
                  <div className="rounded-xl p-4 bg-[#F0EFF5] dark:bg-white/5 border border-[#E2DFEA] dark:border-white/10">
                    <p
                      className="text-sm font-semibold text-[#2D2F7E] dark:text-white mb-2"
                      style={{ fontFamily: 'Sora, sans-serif' }}
                    >
                      {t('rewards.claims.eligibleLabel', {
                        amount: totalClaimable.toLocaleString(),
                      })}
                    </p>
                    <ul
                      className="space-y-2 text-sm text-[#464452] dark:text-white/70"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {unclaimedCampaigns.map((c) => (
                        <li
                          key={c.id}
                          className="flex justify-between items-center"
                        >
                          <span>{c.name}</span>
                          <span className="font-medium text-[#936FEF]">
                            {c.amountPet.toLocaleString()} PET
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Button
                    onClick={() => handleClaim(unclaimedCampaigns[0]?.id)}
                    disabled={isClaiming}
                    className="bg-[#936FEF] hover:bg-[#7c5cd4] text-white gap-2"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    {isClaiming ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('rewards.claims.claiming')}
                      </>
                    ) : (
                      <>{t('rewards.claims.claimPet')}</>
                    )}
                  </Button>
                </>
              ) : (
                <div className="rounded-xl p-6 bg-[#F0EFF5] dark:bg-white/5 border border-[#E2DFEA] dark:border-white/10 text-center">
                  {allClaimed ? (
                    <>
                      <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                      <p
                        className="text-[#2D2F7E] dark:text-white font-semibold"
                        style={{ fontFamily: 'Sora, sans-serif' }}
                      >
                        {t('rewards.claims.allAirdropsClaimed')}
                      </p>
                      {claimedTxRef && (
                        <p
                          className="text-xs text-[#464452] dark:text-white/60 mt-1"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          Tx: {claimedTxRef.slice(0, 12)}...
                        </p>
                      )}
                    </>
                  ) : (
                    <p
                      className="text-[#464452] dark:text-white/70"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('rewards.claims.noUnclaimedAirdrops')}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {eligibility &&
            eligibility.eligible &&
            unclaimedCampaigns.length > 0 && (
              <button
                type="button"
                onClick={() => checkEligibility()}
                className="text-sm text-[#936FEF] hover:underline"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('rewards.claims.refreshEligibility')}
              </button>
            )}
        </div>
      )}
    </div>
  );
}

// --- ICO Claims card ---

interface IcoClaimsCardProps {
  walletAddress: string | null;
  onConnectWallet: () => void;
  onDisconnect: () => void;
  onClaimSuccess: () => void;
}

function IcoClaimsCard({
  walletAddress,
  onConnectWallet,
  onDisconnect,
  onClaimSuccess,
}: IcoClaimsCardProps) {
  const { t } = useTranslation();
  const [investment, setInvestment] = useState<IcoInvestmentResponse | null>(
    null
  );
  const [isVerifying, setIsVerifying] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const verifyInvestment = useCallback(async () => {
    if (!walletAddress) return;
    setIsVerifying(true);
    setError(null);
    setInvestment(null);
    try {
      const res = await verifyIcoInvestment(walletAddress);
      setInvestment(res);
    } catch (err) {
      const msg = getApiErrorMessage(
        err,
        t('rewards.claims.failedToVerifyIco')
      );
      setError(msg);
      toast.error(msg);
    } finally {
      setIsVerifying(false);
    }
  }, [walletAddress]);

  useEffect(() => {
    if (walletAddress && investment === null && !isVerifying && !error) {
      verifyInvestment();
    }
  }, [walletAddress, investment, isVerifying, error, verifyInvestment]);

  const handleClaim = async () => {
    if (
      !walletAddress ||
      !investment?.claimableNow ||
      investment.claimableNow <= 0
    )
      return;
    setIsClaiming(true);
    setError(null);
    try {
      const res = await claimIco({
        walletAddress,
        amount: investment.claimableNow,
      });
      if (res.success) {
        toast.success(t('rewards.claims.icoClaimedToast'), {
          description: t('rewards.claims.icoClaimedDesc', {
            amount: res.claimedAmount,
          }),
        });
        onClaimSuccess();
        setInvestment(null);
        verifyInvestment();
      } else {
        toast.error(res.message || t('rewards.claims.claimFailed'));
      }
    } catch (err) {
      const msg = getApiErrorMessage(err, t('rewards.claims.failedToClaimIco'));
      setError(msg);
      toast.error(msg);
    } finally {
      setIsClaiming(false);
    }
  };

  const claimableNow = investment?.claimableNow ?? 0;
  const hasClaimable = investment?.found && claimableNow > 0;

  return (
    <div className="rounded-2xl border border-[#E2DFEA] dark:border-white/10 bg-white dark:bg-[#16151D] p-6">
      <p
        className="text-[#464452] dark:text-white/70 mb-6"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        {t('rewards.claims.claimIcoDesc')}
      </p>

      {!walletAddress ? (
        <div className="flex flex-col items-center gap-4">
          <Button
            onClick={onConnectWallet}
            className="bg-[#936FEF] hover:bg-[#7c5cd4] text-white gap-2"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
          >
            <Wallet className="w-5 h-5" />
            {t('rewards.claims.connectWallet')}
          </Button>
          <p
            className="text-xs text-[#464452] dark:text-white/60"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('rewards.claims.usePresaleWallet')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span
                className="text-sm font-mono text-[#464452] dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}
              </span>
              <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                <CheckCircle className="w-3.5 h-3.5" />
                {t('rewards.claims.walletConnected')}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDisconnect}
              className="text-[#464452] dark:text-white/70"
            >
              {t('rewards.claims.disconnect')}
            </Button>
          </div>

          {isVerifying && (
            <div className="flex items-center gap-2 py-6 text-[#464452] dark:text-white/70">
              <Loader2 className="w-5 h-5 animate-spin text-[#936FEF]" />
              <span style={{ fontFamily: 'Inter, sans-serif' }}>
                {t('rewards.claims.verifyingInvestment')}
              </span>
            </div>
          )}

          {error && !isVerifying && (
            <div className="flex items-start gap-2 p-4 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50">
              <AlertCircle className="w-5 h-5 shrink-0 text-red-600 dark:text-red-400" />
              <p
                className="text-sm text-red-700 dark:text-red-300"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {error}
              </p>
            </div>
          )}

          {investment && !investment.found && !isVerifying && (
            <div className="rounded-xl p-6 bg-[#F0EFF5] dark:bg-white/5 border border-[#E2DFEA] dark:border-white/10 text-center">
              <p
                className="text-[#2D2F7E] dark:text-white mb-2"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
              >
                {t('rewards.claims.noIcoParticipation')}
              </p>
              <a
                href="mailto:hello@petso.io"
                className="inline-flex items-center gap-1 text-sm text-[#936FEF] hover:underline"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('rewards.claims.contactSupport')}
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          )}

          {investment?.found && !isVerifying && (
            <div className="space-y-4">
              <div className="rounded-xl p-4 bg-[#F0EFF5] dark:bg-white/5 border border-[#E2DFEA] dark:border-white/10 space-y-3">
                {investment.totalPetPurchased != null && (
                  <p
                    className="text-sm text-[#464452] dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('rewards.claims.totalPetPurchased')}{' '}
                    <span className="font-semibold text-[#2D2F7E] dark:text-white">
                      {investment.totalPetPurchased.toLocaleString()}
                    </span>
                  </p>
                )}
                {investment.alreadyClaimed != null && (
                  <p
                    className="text-sm text-[#464452] dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('rewards.claims.alreadyClaimed')}{' '}
                    <span className="font-semibold">
                      {investment.alreadyClaimed.toLocaleString()} PET
                    </span>
                  </p>
                )}
                {investment.vesting && (
                  <p
                    className="text-sm text-[#464452] dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('rewards.claims.vesting')}{' '}
                    {investment.vesting.vestedSoFar?.toLocaleString() ?? 0} /{' '}
                    {investment.vesting.totalVestingPet?.toLocaleString() ?? 0}{' '}
                    PET
                    {investment.vesting.nextUnlockAt && (
                      <span className="block mt-1 text-xs">
                        {t('rewards.claims.nextUnlock')}{' '}
                        {investment.vesting.nextUnlockAt}
                      </span>
                    )}
                  </p>
                )}
                <p
                  className="text-sm font-semibold text-[#2D2F7E] dark:text-white"
                  style={{ fontFamily: 'Sora, sans-serif' }}
                >
                  {t('rewards.claims.availableToClaimNow', {
                    amount: claimableNow.toLocaleString(),
                  })}
                </p>
              </div>

              {hasClaimable ? (
                <Button
                  onClick={handleClaim}
                  disabled={isClaiming}
                  className="bg-[#936FEF] hover:bg-[#7c5cd4] text-white gap-2"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {isClaiming ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('rewards.claims.claiming')}
                    </>
                  ) : (
                    <>{t('rewards.claims.claimAvailablePet')}</>
                  )}
                </Button>
              ) : (
                <div className="rounded-xl p-4 bg-[#F0EFF5] dark:bg-white/5 border border-[#E2DFEA] dark:border-white/10 text-center">
                  <p
                    className="text-sm text-[#464452] dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('rewards.claims.noAmountToClaimNow')}
                    {investment.vesting?.nextUnlockAt && (
                      <span className="block mt-1">
                        {t('rewards.claims.nextClaim')}{' '}
                        {investment.vesting.nextUnlockAt}
                      </span>
                    )}
                  </p>
                </div>
              )}

              {investment.claimHistory &&
                investment.claimHistory.length > 0 && (
                  <div>
                    <p
                      className="text-sm font-semibold text-[#2D2F7E] dark:text-white mb-2"
                      style={{ fontFamily: 'Sora, sans-serif' }}
                    >
                      {t('rewards.claims.claimHistory')}
                    </p>
                    <ul
                      className="space-y-1 text-sm text-[#464452] dark:text-white/70"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {investment.claimHistory.map((h, i) => (
                        <li key={i}>
                          {h.amount.toLocaleString()} PET —{' '}
                          {new Date(h.claimedAt).toLocaleDateString()}
                          {h.transactionReference &&
                            ` (${h.transactionReference.slice(0, 8)}...)`}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

              <button
                type="button"
                onClick={() => verifyInvestment()}
                className="text-sm text-[#936FEF] hover:underline"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('rewards.claims.refreshVerification')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
